import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, catchError, firstValueFrom, lastValueFrom, throwError } from 'rxjs';
import { AuthService } from '../auth/services/auth.service';
import { SessionService } from '../auth/services/session.service';
import {
  getTokenFromAuthCode,
  isWithinFiveMinutes,
  silentAuthentication,
} from '../shared/helpers/auth-utils/token.refresh.utils';
import { CONFIG } from '../shared/models/config';

const TOKEN_URL = `${CONFIG.authUrl}/auth/token`;
const CHOSEN_TOKEN_URL = `${CONFIG.authUrl}/auth/token/chosen`;

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(
    private http: HttpClient,
    private auth: AuthService,
    private sessionService: SessionService,
    @Inject('APP_CONFIG') private config: { apiUrl: string },
  ) {}

  async get<T>(url: string, params = {}): Promise<T> {
    const request = this.http.get<T>(`${this.config.apiUrl}/${url}`, {});
    return lastValueFrom(request);
  }

  private checkIfRefreshNeeded() {
    const currentSessionExpiry = this.sessionService.session.getValue()?.exp;
    if (currentSessionExpiry) {
      if (isWithinFiveMinutes(currentSessionExpiry)) {
        this.performSilentAuth();
      }
    }
  }

  reAuthenticate(id: string) {
    this.performSilentAuth(id);
  }

  private async performSilentAuth(id?: string) {
    const authCode = await silentAuthentication();
    const { body, httpOptions } = getTokenFromAuthCode(authCode, id);
    const token = await firstValueFrom(this.http.post(CHOSEN_TOKEN_URL, body.toString(), httpOptions));
    this.sessionService.setToken(token);
  }

  public getAsObservable<T>(url: string) {
    this.checkIfRefreshNeeded();

    return this.http.get<T>(`${this.config.apiUrl}/${url}`).pipe(
      catchError((e) =>
        throwError(() => {
          if (e.status === 401) {
            this.auth.logout(true);
          } else {
            throwError(() => e);
          }
        }),
      ),
    );
  }

  public postAsObservable<T>(url: string, entity?: any, responseType?: any): Observable<T> {
    this.checkIfRefreshNeeded();

    return this.http
      .post<T>(`${this.config.apiUrl}/${url}`, entity, { responseType })
      .pipe(catchError((e) => throwError(() => e)));
  }

  public putAsObservable<T>(url: string, entity: any, headers?: HttpHeaders): Observable<T> {
    this.checkIfRefreshNeeded();

    return this.http
      .put<T>(`${this.config.apiUrl}/${url}`, entity, { headers })
      .pipe(catchError((e) => throwError(() => e)));
  }

  async put<T>(url: string, entry: any): Promise<T> {
    const request = this.http.put<T>(`${this.config.apiUrl}/${url}`, entry);
    return lastValueFrom(request);
  }

  async post<T>(url: string, entry: any): Promise<T> {
    const request = this.http.post<T>(`${this.config.apiUrl}/${url}`, entry);
    return lastValueFrom(request);
  }
}
